"use client"

import { useCallback, useEffect, useId, useRef, useState } from "react"
import { createPortal } from "react-dom"
import "./HoverTooltip.css"

const DEFAULT_MAX_WIDTH = 320
const DEFAULT_GAP = 8
const DEFAULT_Z_INDEX = 10050

function snapshotRect(el) {
  const r = el.getBoundingClientRect()
  return {
    top: r.top,
    left: r.left,
    right: r.right,
    bottom: r.bottom,
    width: r.width,
    height: r.height,
  }
}

function isUsableRect(rect) {
  return Boolean(rect && (rect.width > 0 || rect.height > 0))
}

function getPortalStyle(coords, { placement, align, gap, maxWidth, zIndex, compact }) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024
  const vh = typeof window !== "undefined" ? window.innerHeight : 768
  const tipWidth = compact ? null : Math.min(maxWidth, vw - 24)
  const base = {
    position: "fixed",
    zIndex,
    transform: "none",
    width: compact ? "max-content" : `${tipWidth}px`,
  }

  const horizontal =
    align === "end"
      ? { left: "auto", right: Math.max(12, vw - coords.right) }
      : align === "start"
        ? { left: Math.max(12, coords.left), right: "auto" }
        : tipWidth
          ? {
              left: Math.max(12, Math.min(coords.left + coords.width / 2 - tipWidth / 2, vw - tipWidth - 12)),
              right: "auto",
            }
          : { left: coords.left + coords.width / 2, right: "auto", transform: "translateX(-50%)" }

  if (placement === "right") {
    return {
      ...base,
      left: coords.right + gap,
      top: coords.top + coords.height / 2,
      transform: "translateY(-50%)",
    }
  }

  if (placement === "left") {
    return {
      ...base,
      left: "auto",
      right: vw - coords.left + gap,
      top: coords.top + coords.height / 2,
      transform: "translateY(-50%)",
    }
  }

  if (placement === "above") {
    return {
      ...base,
      ...horizontal,
      top: "auto",
      bottom: Math.max(12, vh - coords.top + gap),
    }
  }

  return {
    ...base,
    ...horizontal,
    top: coords.bottom + gap,
  }
}

/**
 * Shared hover tooltip for the app.
 *
 * @param {"portal"|"inline"} strategy
 * @param {"above"|"below"|"right"|"left"} placement
 * @param {"center"|"start"|"end"} align — horizontal anchor for above/below
 * @param {boolean} compact — single-line compact style (sidebar, audits)
 * @param {"dark"|"light"} variant — dark (default) or calendar-style white + border
 * @param {boolean} [open] — controlled visibility (inline field hints)
 */
export default function HoverTooltip({
  content,
  children,
  strategy = "portal",
  placement = "below",
  align = "center",
  compact = false,
  variant = "dark",
  open: openControlled,
  className = "",
  wrapClassName = "",
  ariaLabel = "More information",
  maxWidth = DEFAULT_MAX_WIDTH,
  gap = DEFAULT_GAP,
  zIndex = DEFAULT_Z_INDEX,
}) {
  const text = String(content ?? "").trim()
  const tipId = useId()
  const rootRef = useRef(null)
  const [hovered, setHovered] = useState(false)
  const [coords, setCoords] = useState(null)

  const isControlled = openControlled !== undefined
  const active = Boolean(text) && (isControlled ? openControlled : hovered)

  const updateCoords = useCallback(() => {
    const el = rootRef.current
    if (!el) return
    const next = snapshotRect(el)
    if (!isUsableRect(next)) return
    setCoords(next)
  }, [])

  useEffect(() => {
    if (!active || strategy !== "portal") return
    updateCoords()
    const onScrollOrResize = () => updateCoords()
    window.addEventListener("scroll", onScrollOrResize, true)
    window.addEventListener("resize", onScrollOrResize)
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true)
      window.removeEventListener("resize", onScrollOrResize)
    }
  }, [active, strategy, updateCoords])

  if (!text) return children ?? null

  const placementClass = `soho-hover-tooltip--${placement}`
  const strategyClass = strategy === "inline" ? "soho-hover-tooltip--inline" : "soho-hover-tooltip--portaled"
  const compactClass = compact ? " soho-hover-tooltip--compact" : ""
  const variantClass = variant === "light" ? " soho-hover-tooltip--light" : ""
  const alignClass = align !== "center" ? ` soho-hover-tooltip--align-${align}` : ""

  const tooltipNode = (
    <div
      id={tipId}
      role="tooltip"
      className={`soho-hover-tooltip ${strategyClass} ${placementClass}${compactClass}${variantClass}${alignClass}${className ? ` ${className}` : ""}`}
      style={
        strategy === "portal" && isUsableRect(coords)
          ? getPortalStyle(coords, { placement, align, gap, maxWidth, zIndex, compact })
          : undefined
      }
    >
      {text}
    </div>
  )

  if (strategy === "inline") {
    return (
      <div
        ref={rootRef}
        className={`soho-hover-tooltip-wrap${wrapClassName ? ` ${wrapClassName}` : ""}`}
        tabIndex={0}
        aria-label={ariaLabel}
        aria-describedby={active ? tipId : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
      >
        {children}
        {active ? tooltipNode : null}
      </div>
    )
  }

  const showPortal = active && isUsableRect(coords)

  return (
    <>
      <div
        ref={rootRef}
        className={`soho-hover-tooltip-wrap${wrapClassName ? ` ${wrapClassName}` : ""}`}
        tabIndex={0}
        aria-label={ariaLabel}
        aria-describedby={showPortal ? tipId : undefined}
        onMouseEnter={() => {
          setHovered(true)
          requestAnimationFrame(updateCoords)
        }}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => {
          setHovered(true)
          requestAnimationFrame(updateCoords)
        }}
        onBlur={() => setHovered(false)}
      >
        {children}
      </div>

      {showPortal && typeof document !== "undefined"
        ? createPortal(tooltipNode, document.body)
        : null}
    </>
  )
}
